import { gql, TypedDocumentNode } from "@apollo/client";
import { projectFragment } from "./newProject";
import { teamMemberFragment } from "./teamMembers";
import { employeeFragment } from "./newEmployee";
import {
  employeeReferenceFragment,
  newReferenceShortFragment,
  projectReferenceFragment,
} from "./references";
import { clientFields } from "./newClient";
import {
  CountDraftsAndTranslationsQueryQuery,
  CountDraftsAndTranslationsQueryQueryVariables,
  OwnStillEditingDraftsQueryQuery,
  OwnStillEditingDraftsQueryQueryVariables,
  ReadyDraftsQueryQuery,
  ReadyDraftsQueryQueryVariables,
  StillEditingDraftsQueryQuery,
  StillEditingDraftsQueryQueryVariables,
  VersionedEntity,
  ApplyNewProjectDraftMutationMutation,
  ApplyNewProjectDraftMutationMutationVariables,
  RejectNewProjectDraftMutationMutation,
  RejectNewProjectDraftMutationMutationVariables,
  ResetNewProjectDraftMutationMutation,
  ResetNewProjectDraftMutationMutationVariables,
  ApplyEmployeeDraftMutationMutation,
  ApplyEmployeeDraftMutationMutationVariables,
  RejectEmployeeDraftMutationMutation,
  RejectEmployeeDraftMutationMutationVariables,
  ResetEmployeeDraftMutationMutation,
  ResetEmployeeDraftMutationMutationVariables,
  ApplyClientDraftMutationMutationVariables,
  RejectClientDraftMutationMutation,
  RejectClientDraftMutationMutationVariables,
  ApplyClientDraftMutationMutation,
  ResetClientDraftMutationMutation,
  ResetClientDraftMutationMutationVariables,
  ApplyReferenceDraftMutationMutation,
  ApplyReferenceDraftMutationMutationVariables,
  RejectReferenceDraftMutationMutation,
  RejectReferenceDraftMutationMutationVariables,
  ResetReferenceDraftMutationMutation,
  ResetReferenceDraftMutationMutationVariables,
  ApplyProjectTeamMemberDraftMutationMutation,
  ApplyProjectTeamMemberDraftMutationMutationVariables,
  RejectProjectTeamMemberDraftMutationMutation,
  RejectProjectTeamMemberDraftMutationMutationVariables,
  ResetProjectTeamMemberDraftMutationMutation,
  ResetProjectTeamMemberDraftMutationMutationVariables,
} from "generated/graphql";

export const COUNT_DRAFTS_AND_TRANSLATIONS: TypedDocumentNode<
  CountDraftsAndTranslationsQueryQuery,
  CountDraftsAndTranslationsQueryQueryVariables
> = gql`
  query CountDraftsAndTranslationsQuery {
    countReadyForReviewDrafts: countDrafts(status: "ready")
    countNotReadyForReviewDrafts: countDrafts(status: "notReady")
    countOutdatedTranslations
  }
`;

const draftsMetadataFragment: TypedDocumentNode<VersionedEntity> = gql`
  fragment draftsMetadata_fields on VersionedEntity {
    modifiedBy {
      __typename
      ... on AutomatedChanger {
        automatedId: id
      }
      ... on Employee {
        id
        fullName
      }
    }
    createdBy {
      __typename
      ... on AutomatedChanger {
        automatedId: id
      }
      ... on Employee {
        id
        fullName
      }
    }
    updatedAt
    insertedAt
    deletedAt
    isReadyForReview
  }
`;

export const READY_FOR_REVIEW_DRAFTS: TypedDocumentNode<
  ReadyDraftsQueryQuery,
  ReadyDraftsQueryQueryVariables
> = gql`
  ${draftsMetadataFragment}
  ${teamMemberFragment}
  ${projectFragment}
  ${employeeFragment}
  ${newReferenceShortFragment}
  ${clientFields}
  ${projectReferenceFragment}
  ${employeeReferenceFragment}

  query ReadyDraftsQuery {
    projectDrafts: newProjectDrafts(includeUnlisted: true, status: "ready") {
      id
      ...Project_fields
      ...draftsMetadata_fields
      projectReferences {
        ...ProjectReference_fields
      }
      approved {
        id
        ...Project_fields
        projectReferences {
          ...ProjectReference_fields
        }
      }
    }
    employeeDrafts(status: "ready") {
      id
      ...Employee_fields
      ...draftsMetadata_fields
      references {
        ...EmployeeReference_fields
      }
      approved {
        id
        ...Employee_fields
        references {
          ...EmployeeReference_fields
        }
      }
    }
    clientDrafts(includeUnlisted: true, status: "ready") {
      ...Client_fields
      ...draftsMetadata_fields
      approved {
        ...Client_fields
      }
    }
    referenceDrafts(status: "ready") {
      id
      project {
        id
        name {
          id
          de
          en
          fr
          it
        }
        abacusProjectId
        archivedProjectId
        invoicingResponsible {
          id
          activeDirectoryId
          isFormerEmployee
        }
      }
      ...NewReference_shortFields
      employee {
        id
        fullName
      }
      ...draftsMetadata_fields
      approved {
        id
        project {
          id
          name {
            id
            de
            en
            fr
            it
          }
          abacusProjectId
          archivedProjectId
          invoicingResponsible {
            id
            activeDirectoryId
            isFormerEmployee
          }
        }
        ...NewReference_shortFields
        employee {
          id
          fullName
        }
      }
    }
    projectTeamMemberDrafts(status: "ready") {
      ...teamMember_fields
      ...draftsMetadata_fields
      approved {
        ...teamMember_fields
      }
    }
  }
`;

export const NOT_READY_FOR_REVIEW_DRAFTS: TypedDocumentNode<
  StillEditingDraftsQueryQuery,
  StillEditingDraftsQueryQueryVariables
> = gql`
  ${draftsMetadataFragment}
  ${teamMemberFragment}
  ${projectFragment}
  ${employeeFragment}
  ${newReferenceShortFragment}
  ${clientFields}
  ${projectReferenceFragment}
  ${employeeReferenceFragment}

  query StillEditingDraftsQuery {
    projectDrafts: newProjectDrafts(includeUnlisted: true, status: "notReady") {
      id
      ...Project_fields
      ...draftsMetadata_fields
      projectReferences {
        ...ProjectReference_fields
      }
      approved {
        id
        ...Project_fields
        projectReferences {
          ...ProjectReference_fields
        }
      }
    }
    employeeDrafts(status: "notReady") {
      id
      ...Employee_fields
      ...draftsMetadata_fields
      references {
        ...EmployeeReference_fields
      }
      approved {
        id
        ...Employee_fields
        references {
          ...EmployeeReference_fields
        }
      }
    }
    clientDrafts(includeUnlisted: true, status: "notReady") {
      ...Client_fields
      ...draftsMetadata_fields
      approved {
        ...Client_fields
      }
    }
    referenceDrafts(status: "notReady") {
      id
      project {
        id
        name {
          id
          de
          en
          fr
          it
        }
        abacusProjectId
        archivedProjectId
        invoicingResponsible {
          id
          activeDirectoryId
          isFormerEmployee
        }
      }
      ...NewReference_shortFields
      employee {
        id
        fullName
      }
      ...draftsMetadata_fields
      approved {
        id
        project {
          id
          name {
            id
            de
            en
            fr
            it
          }
          abacusProjectId
          archivedProjectId
          invoicingResponsible {
            id
            activeDirectoryId
            isFormerEmployee
          }
        }
        ...NewReference_shortFields
        employee {
          id
          fullName
        }
      }
    }
    projectTeamMemberDrafts(status: "notReady") {
      ...teamMember_fields
      ...draftsMetadata_fields
      approved {
        ...teamMember_fields
      }
    }
  }
`;

export const EMPLOYEE_NOT_READY_FOR_REVIEW_DRAFTS: TypedDocumentNode<
  OwnStillEditingDraftsQueryQuery,
  OwnStillEditingDraftsQueryQueryVariables
> = gql`
  ${draftsMetadataFragment}

  query OwnStillEditingDraftsQuery($id: Int!) {
    projectDrafts: newProjectDrafts(
      includeUnlisted: true
      status: "notReady"
      createdBy: $id
    ) {
      id
      ...draftsMetadata_fields
      approved {
        id
        name {
          id
          de
          en
          fr
          it
        }
        abacusProjectId
      }
    }
    employeeDrafts(status: "notReady", createdBy: $id) {
      id
      ...draftsMetadata_fields
      approved {
        id
        fullName
        colorProfilePicture(size: 504)
      }
    }
    clientDrafts(includeUnlisted: true, status: "notReady", createdBy: $id) {
      id
      ...draftsMetadata_fields
      approved {
        id
        name
      }
    }
    referenceDrafts(status: "notReady", createdBy: $id) {
      id
      ...draftsMetadata_fields
      projectId
      employeeId
      project {
        id
        name {
          id
          de
          en
          fr
          it
        }
        abacusProjectId
        archivedProjectId
      }
      employee {
        id
        fullName
      }
      approved {
        id
        projectId
        employeeId
        project {
          id
          name {
            id
            de
            en
            fr
            it
          }
          abacusProjectId
          archivedProjectId
        }
        employee {
          id
          fullName
        }
      }
    }
  }
`;

export const APPROVE_PROJECT_DRAFT_MUTATION: TypedDocumentNode<
  ApplyNewProjectDraftMutationMutation,
  ApplyNewProjectDraftMutationMutationVariables
> = gql`
  mutation ApplyNewProjectDraftMutation($id: ID!) {
    applyNewProjectDraft(id: $id) {
      id
      name {
        id
        de
        en
        fr
        it
      }
    }
  }
`;

export const REJECT_PROJECT_DRAFT_MUTATION: TypedDocumentNode<
  RejectNewProjectDraftMutationMutation,
  RejectNewProjectDraftMutationMutationVariables
> = gql`
  mutation RejectNewProjectDraftMutation($id: ID!) {
    rejectNewProjectDraft(id: $id) {
      id
      name {
        id
        de
        en
        fr
        it
      }
    }
  }
`;

export const RESET_PROJECT_DRAFT_MUTATION: TypedDocumentNode<
  ResetNewProjectDraftMutationMutation,
  ResetNewProjectDraftMutationMutationVariables
> = gql`
  mutation ResetNewProjectDraftMutation($id: ID!) {
    editNewProject(id: $id, fields: { isReadyForReview: false }) {
      id
      name {
        id
        de
        en
        fr
        it
      }
    }
  }
`;

export const APPROVE_EMPLOYEE_DRAFT_MUTATION: TypedDocumentNode<
  ApplyEmployeeDraftMutationMutation,
  ApplyEmployeeDraftMutationMutationVariables
> = gql`
  ${employeeFragment}

  mutation ApplyEmployeeDraftMutation($id: ID!) {
    applyEmployeeDraft(id: $id) {
      id
      ...Employee_fields
    }
  }
`;

export const REJECT_EMPLOYEE_DRAFT_MUTATION: TypedDocumentNode<
  RejectEmployeeDraftMutationMutation,
  RejectEmployeeDraftMutationMutationVariables
> = gql`
  ${employeeFragment}

  mutation RejectEmployeeDraftMutation($id: ID!) {
    rejectEmployeeDraft(id: $id) {
      id
      ...Employee_fields
    }
  }
`;

export const RESET_EMPLOYEE_DRAFT_MUTATION: TypedDocumentNode<
  ResetEmployeeDraftMutationMutation,
  ResetEmployeeDraftMutationMutationVariables
> = gql`
  ${employeeFragment}

  mutation ResetEmployeeDraftMutation($id: ID!) {
    editEmployee(id: $id, fields: { isReadyForReview: false }) {
      id
      ...Employee_fields
    }
  }
`;

export const APPROVE_CLIENT_DRAFT_MUTATION: TypedDocumentNode<
  ApplyClientDraftMutationMutation,
  ApplyClientDraftMutationMutationVariables
> = gql`
  mutation ApplyClientDraftMutation($id: ID!) {
    applyClientDraft(id: $id) {
      id
      name
    }
  }
`;

export const REJECT_CLIENT_DRAFT_MUTATION: TypedDocumentNode<
  RejectClientDraftMutationMutation,
  RejectClientDraftMutationMutationVariables
> = gql`
  mutation RejectClientDraftMutation($id: ID!) {
    rejectClientDraft(id: $id) {
      id
      name
    }
  }
`;

export const RESET_CLIENT_DRAFT_MUTATION: TypedDocumentNode<
  ResetClientDraftMutationMutation,
  ResetClientDraftMutationMutationVariables
> = gql`
  mutation ResetClientDraftMutation($id: ID!) {
    editClient(id: $id, fields: { isReadyForReview: false }) {
      id
      name
    }
  }
`;

export const APPROVE_REFERENCE_DRAFT_MUTATION: TypedDocumentNode<
  ApplyReferenceDraftMutationMutation,
  ApplyReferenceDraftMutationMutationVariables
> = gql`
  mutation ApplyReferenceDraftMutation($id: ID!) {
    applyNewReferenceDraft(id: $id) {
      id
      description {
        id
        de
        en
        fr
        it
      }
    }
  }
`;

export const REJECT_REFERENCE_DRAFT_MUTATION: TypedDocumentNode<
  RejectReferenceDraftMutationMutation,
  RejectReferenceDraftMutationMutationVariables
> = gql`
  mutation RejectReferenceDraftMutation($id: ID!) {
    rejectNewReferenceDraft(id: $id) {
      id
      description {
        id
        de
        en
        fr
        it
      }
    }
  }
`;

export const RESET_REFERENCE_DRAFT_MUTATION: TypedDocumentNode<
  ResetReferenceDraftMutationMutation,
  ResetReferenceDraftMutationMutationVariables
> = gql`
  mutation ResetReferenceDraftMutation($id: ID!) {
    editNewReference(id: $id, fields: { isReadyForReview: false }) {
      id
      description {
        id
        de
        en
        fr
        it
      }
    }
  }
`;

export const APPROVE_TEAM_MEMBER_DRAFT_MUTATION: TypedDocumentNode<
  ApplyProjectTeamMemberDraftMutationMutation,
  ApplyProjectTeamMemberDraftMutationMutationVariables
> = gql`
  ${teamMemberFragment}

  mutation ApplyProjectTeamMemberDraftMutation($id: ID!) {
    applyProjectTeamMemberDraft(id: $id) {
      ...teamMember_fields
    }
  }
`;

export const REJECT_TEAM_MEMBER_DRAFT_MUTATION: TypedDocumentNode<
  RejectProjectTeamMemberDraftMutationMutation,
  RejectProjectTeamMemberDraftMutationMutationVariables
> = gql`
  ${teamMemberFragment}

  mutation RejectProjectTeamMemberDraftMutation($id: ID!) {
    rejectProjectTeamMemberDraft(id: $id) {
      ...teamMember_fields
    }
  }
`;

export const RESET_TEAM_MEMBER_DRAFT_MUTATION: TypedDocumentNode<
  ResetProjectTeamMemberDraftMutationMutation,
  ResetProjectTeamMemberDraftMutationMutationVariables
> = gql`
  ${teamMemberFragment}

  mutation ResetProjectTeamMemberDraftMutation(
    $id: ID!
    $employeeId: ID!
    $projectId: ID!
  ) {
    editProjectTeamMember(
      id: $id
      fields: {
        employeeId: $employeeId
        projectId: $projectId
        isReadyForReview: false
      }
    ) {
      ...teamMember_fields
    }
  }
`;
